import type { Account } from "@/redux/api/accounts.rtkq";

/**
 * Checks if 24 hours have passed since the account was created.
 * Returns false for undefined createdAt (legacy accounts without createdAt field).
 * @param createdAt - The account creation date
 * @returns true if 24+ hours have passed, false otherwise
 */
export const has24HoursPassed = (createdAt?: Date): boolean => {
  if (!createdAt) {
    return false; // Legacy accounts without createdAt - no enforcement
  }

  const now = new Date();
  const hoursPassed = (now.getTime() - createdAt.getTime()) / (1000 * 60 * 60);
  return hoursPassed >= 24;
};

/**
 * Determines if profile completion should be enforced.
 * Returns true if:
 * - User is NOT already on /account page (prevents redirect loop)
 * - Account data is loaded
 * - Profile is incomplete (isProfileComplete === false)
 * - 24 hours have passed since account creation
 *
 * @param account - The account data (can be undefined/null while loading)
 * @param currentPath - The current pathname from React Router location
 * @returns true if enforcement should redirect user to /account
 */
export const shouldEnforceProfileCompletion = (
  account: Account | null | undefined,
  currentPath: string
): boolean => {
  // Don't redirect if already on account page (prevents redirect loop)
  if (currentPath === "/account") {
    return false;
  }

  // Don't enforce if account data not loaded yet
  if (!account) {
    return false;
  }

  // Don't enforce if profile is already complete
  if (account.accountInformation.isProfileComplete === true) {
    return false;
  }

  // Enforce if 24+ hours passed and profile is incomplete
  return has24HoursPassed(account.createdAt);
};
